import React, { useEffect, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Image } from 'primereact/image';
import { InputTextarea } from 'primereact/inputtextarea';
import AwsImage from '../../../components/AwsImage';
import { AGENT_TYPES, getAgentIntelligenceLevel, getIntelligenceLevelLabel } from '../agentOptions';
import { useFetchZonesAll, useRunIAMutation } from '../Services/query';

const getResponseContent = (response) => {
    const content = response?.output ?? response?.message ?? response?.content;
    return typeof content === 'string' ? content : JSON.stringify(content ?? response ?? '');
};

const ModalTestIA = ({ agent }) => {
    const { data: zones, isLoading: loadingZones } = useFetchZonesAll();
    const runMutation = useRunIAMutation();
    const [selectedZoneId, setSelectedZoneId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const chatRef = useRef(null);

    useEffect(() => {
        setMessages([]);
        setInput('');
    }, [agent?.id]);

    useEffect(() => {
        chatRef.current?.scrollTo({ top: chatRef.current.scrollHeight, behavior: 'smooth' });
    }, [messages, runMutation.isLoading]);

    const zoneOptions = (zones || []).map((zone) => ({ label: zone.name, value: zone.id }));

    const send = () => {
        const content = input.trim();
        if (!agent || !selectedZoneId || !content || runMutation.isLoading) return;
        setMessages((current) => [...current, { role: 'user', content }]);
        setInput('');
        runMutation.mutate({ agentId: agent.id, input: content, zoneId: selectedZoneId }, {
            onSuccess: (response) => setMessages((current) => [...current, {
                role: 'assistant',
                content: getResponseContent(response),
                citations: response?.citations || [],
                searchResults: response?.search_results || []
            }]),
            onError: (error) => setMessages((current) => [...current, {
                role: 'assistant',
                isError: true,
                content: error?.response?.data?.message || 'Erro ao obter resposta do assistente.'
            }])
        });
    };

    return (
        <div className="flex flex-column h-full surface-card">
            <div className="flex align-items-center justify-content-between px-4 py-3 border-bottom-1 surface-border">
                <span className="font-semibold">Testar Assistente</span>
                {messages.length > 0 && <Button label="Limpar" icon="pi pi-refresh" text size="small" severity="secondary" onClick={() => setMessages([])} />}
            </div>

            {agent && (
                <div className="px-4 py-3 border-bottom-1 surface-border">
                    <Dropdown value={selectedZoneId} options={zoneOptions} loading={loadingZones} onChange={(event) => setSelectedZoneId(event.value)} placeholder="Selecione a zona para o teste" emptyMessage="Nenhuma zona encontrada" className="w-full" />
                </div>
            )}

            <div ref={chatRef} className="flex-1 overflow-y-auto p-4 flex flex-column gap-3" style={{ background: 'var(--surface-ground)', minHeight: 0 }}>
                {!agent ? (
                    <div className="flex-1 flex flex-column align-items-center justify-content-center text-center text-color-secondary px-4">
                        <i className="pi pi-android mb-3" style={{ fontSize: '2.5rem', opacity: 0.25 }} />
                        <div className="font-medium text-color">Termine de configurar o assistente</div>
                        <div className="text-sm mt-2">Depois de salvar, você poderá testá-lo aqui.</div>
                    </div>
                ) : messages.length === 0 ? (
                    <div className="flex-1 flex flex-column align-items-center justify-content-center text-center text-color-secondary px-4">
                        <div className="border-1 surface-border border-round-xl overflow-hidden flex align-items-center justify-content-center surface-card mb-3" style={{ width: 64, height: 64 }}>
                            {agent.aws_fk || agent.aws?.id || agent.aws?.url ? (
                                <AwsImage
                                    awsId={agent.aws_fk ?? agent.aws?.id}
                                    aws={agent.aws}
                                    usage="avatar"
                                    width={64}
                                    height={64}
                                    alt={agent.name}
                                    fallback={<i className="pi pi-android text-primary" style={{ fontSize: '1.75rem' }} />}
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                            ) : <i className="pi pi-android text-primary" style={{ fontSize: '1.75rem' }} />}
                        </div>
                        <div className="font-semibold text-color">{agent.name}</div>
                        <div className="text-sm mt-2">{selectedZoneId ? 'Envie uma mensagem para testar.' : 'Selecione uma zona para começar.'}</div>
                        <div className="text-xs mt-2">{getIntelligenceLevelLabel(getAgentIntelligenceLevel(agent))} · Temp: {agent.temperature}</div>
                    </div>
                ) : messages.map((message, index) => (
                    <div key={index} className={`flex ${message.role === 'user' ? 'justify-content-end' : 'justify-content-start'}`}>
                        <div className={`p-3 border-round-xl ${message.role === 'user' ? 'bg-primary text-primary-color' : message.isError ? 'bg-red-50 text-red-700' : 'surface-card text-color'}`} style={{ maxWidth: '88%', lineHeight: 1.55, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                            {message.role === 'assistant' && agent.type === AGENT_TYPES.IMAGE && !message.isError ? (
                                <Image src={message.content} alt="Imagem gerada pelo assistente" preview width="100%" imageStyle={{ maxWidth: '100%', borderRadius: 8 }} />
                            ) : message.content}
                            {message.citations?.length > 0 && (
                                <div className="mt-3 pt-3 border-top-1 surface-border flex flex-column gap-2">
                                    <small className="font-semibold text-color-secondary"><i className="pi pi-globe mr-1" />FONTES</small>
                                    {message.citations.map((url, citationIndex) => {
                                        const result = message.searchResults?.find((item) => item.url === url);
                                        return <a key={url} href={url} target="_blank" rel="noreferrer" className="text-primary text-sm no-underline"><span className="mr-2">{citationIndex + 1}.</span>{result?.title || url}</a>;
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                ))}
                {runMutation.isLoading && <div className="flex justify-content-start"><div className="surface-card p-3 border-round-xl text-color-secondary"><i className="pi pi-spin pi-spinner mr-2" />Aguardando resposta...</div></div>}
            </div>

            <div className="flex align-items-end gap-2 p-3 border-top-1 surface-border">
                <InputTextarea value={input} onChange={(event) => setInput(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); send(); } }} rows={1} autoResize placeholder="Envie uma mensagem teste" disabled={!agent || !selectedZoneId || runMutation.isLoading} className="flex-1" style={{ maxHeight: 120 }} />
                <Button icon="pi pi-send" onClick={send} loading={runMutation.isLoading} disabled={!agent || !selectedZoneId || !input.trim()} />
            </div>
        </div>
    );
};

export default ModalTestIA;
